import { TAssetsSvgMapValue } from '@libs/types';
import clsx from 'clsx';
import DOMPurify from 'dompurify';
import React, { useEffect, useState } from 'react';
import { getInlineStyles } from '../../utils/styles';
import { fetchAndCacheIcon } from './helper';
import classes from './svg.module.css';
import { Loader } from '../../widgets/UI/Loader/Loader';

type TSvgComponent = {
    fill?: string;
    name: TAssetsSvgMapValue;
    width?: number;
    height?: number;
    defaultName?: string;
    doNotUseWidthAndHeight?: boolean;
    onClick?: () => void;
};

const SvgComponent = ({
    width = 24,
    height = 24,
    fill = 'purple_100',
    name,
    defaultName = 'ic_placeholder',
    doNotUseWidthAndHeight = false,
    onClick,
}: TSvgComponent) => {
    const [svgData, setSvgData] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<Error | null>(null);
    const isDefaultSize = !doNotUseWidthAndHeight && width === 24 && height === 24;
    const isDefaultFell = fill === 'purple_100';
    const styles = getInlineStyles({
        fill: isDefaultFell ? undefined : fill,
        height: doNotUseWidthAndHeight || isDefaultSize ? undefined : `${height}px`,
        width: doNotUseWidthAndHeight || isDefaultSize ? undefined : `${width}px`,
    });
    const classNames = clsx('svgComponent', isDefaultSize && classes.defaultSize, isDefaultFell && classes.defaultFillColor);

    const fetchSVG = async (loadName: string, loadDefaultName: string) => {
        setLoading(true);
        setError(null);
        try {
            const text = await fetchAndCacheIcon(loadName, loadDefaultName);
            if (text) {
                setSvgData(text);
            }
        } catch (err: unknown) {
            setError(err as Error);
            setSvgData(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Reset state when the URL changes
        setSvgData(null);
        fetchSVG(name || defaultName, defaultName).then();
    }, [defaultName, name]);

    if (loading) {
        return <Loader width={width} height={height} fill={styles?.fill} />;
    }
    if (!svgData || error) {
        return <div className={classNames} style={styles} />;
    }

    const sanitizedSvgData = DOMPurify.sanitize(svgData);

    return (
        <div
            onClick={() => {
                if (onClick) {
                    onClick();
                }
            }}
            className={classNames}
            style={styles}
            dangerouslySetInnerHTML={{ __html: sanitizedSvgData }}
        />
    );
};
export { SvgComponent };
